/**
 * @packageDocumentation
 * @module ui-assembler
 */

import { IAssembler } from '../../renderer/base';
import { IRenderData, RenderData } from '../../renderer/render-data';
import { IBatcher } from '../../renderer/i-batcher';
import { Sprite } from '../../components';
import { dynamicAtlasManager } from '../../utils/dynamic-atlas/atlas-manager';
import { StaticVBChunk } from '../../renderer/static-vb-accessor';
import { Mat4, math, v2, Vec2 } from '../../../core';
import { UIRenderer } from '../../framework';

/**
 * polygonal 组装器
 * 可通过 `UI.polygonal` 获取该组装器。
 */
export const polygonal: IAssembler = {
    createData (sprite: UIRenderer) {
        const renderData = sprite.requestRenderData();
        renderData.dataLength = 4;
        renderData.resize(4, 6);
        return renderData;
    },
    updateVerts (sprite: Sprite) {
        const { renderData } = sprite;
        if (!renderData) {
            return;
        }
        const uiTrans = sprite.node._uiProps.uiTransformComp!;
        const dataList: IRenderData[] = renderData.data;
        const cw = uiTrans.width;
        const ch = uiTrans.height;
        const appX = (uiTrans.anchorX - 0.5) * cw;
        const appY = (uiTrans.anchorY - 0.5) * ch;
        const frame = sprite.spriteFrame!;
        const originSize = frame.originalSize;
        const ow = originSize.width;
        const oh = originSize.height;
        const scaleX = cw / ow;
        const scaleY = ch / oh;
        const { offset, rect } = frame;
        const rw = rect.width;
        const rh = rect.height;
        const trimScaleX = rw / ow;
        const trimScaleY = rh / oh;
        for (let i = 0; i < sprite.polygon.length; i++) {
            if (!sprite.polygon[i]) {
                continue;
            }
            if (!sprite.trim) {
                // 計算 sprite 在沒有啟用 trim 時影響的位移和大小
                dataList[i].x = sprite.polygon[i].x * trimScaleX + offset.x;
                dataList[i].y = sprite.polygon[i].y * trimScaleY + offset.y;
            } else {
                dataList[i].x = sprite.polygon[i].x;
                dataList[i].y = sprite.polygon[i].y;
            }
            // 計算 UITransform 中 size 及 anchorPoint 影響的位移和大小
            dataList[i].x = dataList[i].x * scaleX - appX;
            dataList[i].y = dataList[i].y * scaleY - appY;
        }

        renderData.vertDirty = true;
    },

    updateRenderData (sprite: Sprite) {
        const renderData = sprite.renderData!;
        const frame = sprite.spriteFrame!;
        if (!frame || !renderData) {
            return;
        }

        if (!renderData.vertDirty) {
            return;
        }

        const { polygon } = sprite;
        if (!polygon || polygon.length < 3) {
            return;
        }
        // polygon data
        const vertexCount = polygon.length;
        const indexCount = vertexCount + (vertexCount - 3) * 2;

        renderData.dataLength = vertexCount;

        if (renderData.vertexCount !== vertexCount) {
            sprite.renderEntity.colorDirty = true;
        }
        // update data property
        renderData.resize(vertexCount, indexCount);

        this.updateVerts(sprite);
        this.updateUVs(sprite);
        this.updateColor(sprite);

        renderData.updateRenderData(sprite, frame);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    fillBuffers (sprite: Sprite, render: IBatcher) {
        const { node } = sprite;
        const renderData: RenderData = sprite.renderData!;
        const { chunk } = renderData;
        if (node.hasChangedFlags || renderData.vertDirty) {
            this.updateWorldVertex(sprite, chunk);
            renderData.vertDirty = false;
        }

        // update indices
        const vid = chunk.vertexOffset;
        const { meshBuffer } = chunk;
        const ib = chunk.meshBuffer.iData;
        const { indicesArr } = sprite;
        for (let i = 0; i < indicesArr.length; i++) {
            ib[meshBuffer.indexOffset++] = vid + indicesArr[i];
        }

        meshBuffer.setDirty();
    },

    // Too early
    updateColor (sprite: Sprite) {
        const renderData = sprite.renderData!;
        const vData = renderData.chunk.vb;
        const stride = renderData.floatStride;

        let colorOffset = 5;
        const { color } = sprite;
        const colorR = color.r / 255;
        const colorG = color.g / 255;
        const colorB = color.b / 255;
        const colorA = sprite.node._uiProps.opacity;
        for (let i = 0; i < sprite.polygon.length; i++) {
            vData[colorOffset] = colorR;
            vData[colorOffset + 1] = colorG;
            vData[colorOffset + 2] = colorB;
            vData[colorOffset + 3] = colorA;
            colorOffset += stride;
        }
    },

    updateUVs (sprite: Sprite) {
        if (!sprite.spriteFrame) {
            return;
        }
        const renderData = sprite.renderData!;
        const vData = renderData.chunk.vb;
        const stride = renderData.floatStride;
        const { polygon } = sprite;
        const { uv, originalSize } = sprite.spriteFrame; // 用spriteFrame本身的UV來計算每個點在合圖中的UV
        let uvs: Vec2[] = [];
        uvs = computeUv(polygon, uv, originalSize.width, originalSize.height);
        let uvOffset = 3;

        // 更新UV
        for (let i = 0; i < sprite.polygon.length; i++) {
            vData[uvOffset] = uvs[i].x;
            vData[uvOffset + 1] = uvs[i].y;
            uvOffset += stride;
        }

        renderData.vertDirty = true;
        sprite.markForUpdateRenderData();
    },

    updateWorldVertex (sprite: Sprite, chunk: StaticVBChunk) {
        const m = new Mat4();
        const { node } = sprite;
        node.getWorldMatrix(m);

        const renderData = sprite.renderData!;
        const stride = renderData.floatStride;
        const dataList: IRenderData[] = renderData.data;
        const vData = chunk.vb;

        const { length } = dataList;
        for (let i = 0; i < length; i++) {
            const { x, y, z } = dataList[i];
            let rhw = m.m03 * x + m.m07 * y + m.m11 * z + m.m15;
            rhw = rhw ? Math.abs(1 / rhw) : 1;

            const offset = i * stride;
            vData[offset] = (m.m00 * x + m.m04 * y + m.m08 * z + m.m12) * rhw;
            vData[offset + 1] = (m.m01 * x + m.m05 * y + m.m09 * z + m.m13) * rhw;
            vData[offset + 2] = (m.m02 * x + m.m06 * y + m.m10 * z + m.m14) * rhw;
        }
    },
};

/** 计算uv, 锚点都是中心 */
function computeUv (
    points: Vec2[],
    spriteFrameUV: number[],
    width: number,
    height: number,
) {
    const tb = spriteFrameUV.filter((v, i) => i % 2);
    const lr = spriteFrameUV.filter((v, i) => (i + 1) % 2);

    const lrRange = Math.max(...lr) - Math.min(...lr);
    const tbRange = Math.max(...tb) - Math.min(...tb);
    const l = Math.min(...lr);
    const t = Math.min(...tb);
    const uvs: Vec2[] = [];
    points.forEach((p) => {
        // uv原点是左上角
        const x = math.clamp(0, 1, (p.x + width / 2) / width);
        const y = math.clamp(0, 1, 1 - (p.y + height / 2) / height);
        uvs.push(v2(x * lrRange + l, y * tbRange + t));
    });
    return uvs;
}
